import JSZip from "jszip";

/**
 * Service pour gérer les données WER (Word Error Rate)
 * Note: Ce service ne fait PAS d'appels API authentifiés pour éviter les problèmes de déconnexion
 */
const werService = {
  /**
   * Récupère les données WER depuis le localStorage ou fichier
   * @returns {Promise<Array>} Liste des segments avec leurs métriques WER
   */
  async getWERData() {
    // Vérifier d'abord le localStorage
    const cachedData = localStorage.getItem("wer_data");
    if (cachedData) {
      try {
        return JSON.parse(cachedData);
      } catch (error) {
        console.error(
          "Erreur lors du parsing des données WER en cache:",
          error,
        );
      }
    }

    // Retourner un tableau vide si pas de données
    return [];
  },

  /**
   * Sauvegarde les données WER dans le localStorage
   * @param {Array} data - Données WER à sauvegarder
   * @param {boolean} includeAudio - Inclure les données audio base64 (défaut: false)
   */
  async saveWERData(data, includeAudio = false) {
    try {
      // Ne pas sauvegarder les audio_base64 pour économiser l'espace localStorage
      const dataToSave = includeAudio
        ? data
        : data.map((item) => {
            const { audio_base64, ...rest } = item;
            return rest;
          });
      localStorage.setItem("wer_data", JSON.stringify(dataToSave));
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des données WER:", error);
      // Si l'erreur est due au quota, essayer sans les textes de référence
      if (error.name === "QuotaExceededError") {
        try {
          const minimalData = data.map((item) => ({
            segment: item.segment,
            wer: item.wer,
            category: item.category,
            status: item.status,
          }));
          localStorage.setItem("wer_data", JSON.stringify(minimalData));
          console.log("💾 Sauvegarde minimale effectuée (résultats seulement)");
        } catch (e) {
          console.error("Impossible de sauvegarder même les données minimales");
        }
      }
    }
  },

  /**
   * Récupère les statistiques globales WER depuis les données en cache
   * @returns {Promise<Object>} Statistiques agrégées
   */
  async getWERStats() {
    const data = await this.getWERData();
    if (!data || data.length === 0) {
      return {
        totalSegments: 0,
        averageWER: 0,
        excellent: 0,
        good: 0,
        fair: 0,
        poor: 0,
      };
    }

    const totalSegments = data.length;
    const averageWER =
      data.reduce((sum, item) => sum + item.wer, 0) / totalSegments;

    return {
      totalSegments,
      averageWER: averageWER.toFixed(2),
      excellent: data.filter((d) => d.wer < 5).length,
      good: data.filter((d) => d.wer >= 5 && d.wer < 10).length,
      fair: data.filter((d) => d.wer >= 10 && d.wer < 20).length,
      poor: data.filter((d) => d.wer >= 20).length,
    };
  },

  /**
   * Lance un nouveau test WER sur des segments audio
   * Note: Cette fonction est un placeholder pour une future implémentation
   * @param {Object} config - Configuration du test
   * @returns {Promise<Object>} Résultats du test
   */
  async runWERTest(config) {
    console.log("Test WER demandé avec config:", config);
    throw new Error(
      "La fonctionnalité de test WER n'est pas encore implémentée",
    );
  },

  /**
   * Récupère les résultats du dernier benchmark de transcription live (med-dictate)
   * @returns {Array} Résultats {sampleId, modelId, wer, ...} ou tableau vide
   */
  getLiveBenchmarkResults() {
    const cached = localStorage.getItem("live_benchmark_results");
    if (!cached) return [];
    try {
      return JSON.parse(cached);
    } catch (error) {
      console.error(
        "Erreur lors du parsing des résultats de benchmark live:",
        error,
      );
      return [];
    }
  },

  /**
   * Sauvegarde les résultats du benchmark de transcription live
   * @param {Array} results - Résultats à sauvegarder
   */
  saveLiveBenchmarkResults(results) {
    try {
      localStorage.setItem("live_benchmark_results", JSON.stringify(results));
    } catch (error) {
      console.error(
        "Erreur lors de la sauvegarde des résultats de benchmark live:",
        error,
      );
    }
  },

  /**
   * Récupère les détails d'un segment spécifique
   * @param {string} segmentId - ID du segment
   * @returns {Promise<Object>} Détails du segment avec transcription
   */
  async getSegmentDetails(segmentId) {
    const data = await this.getWERData();
    const segment = data.find((d) => d.segment === segmentId);
    if (!segment) {
      throw new Error(`Segment ${segmentId} non trouvé`);
    }
    return segment;
  },

  /**
   * Parse un fichier de résultats WER local
   * @param {File} file - Fichier de résultats (.txt, .json ou .zip)
   * @returns {Promise<Array>} Données parsées
   */
  async parseResultsFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          let data = [];

          // Vérifier si c'est un fichier JSON (dataset complet)
          if (file.name.endsWith(".json")) {
            const jsonData = JSON.parse(text);

            // Format du dataset complet avec segments
            if (jsonData.segments && Array.isArray(jsonData.segments)) {
              data = jsonData.segments.map((segment) => ({
                segment: segment.id,
                reference_text: segment.reference_text,
                audio_file: segment.audio_file,
                audio_base64: segment.audio_base64,
                wer: segment.wer || 0,
                reference_length: segment.reference_text.split(" ").length,
                hypothesis_length: segment.hypothesis_length || 0,
                category: segment.category || "pending",
                status: segment.status || "not_tested",
              }));

              console.log(`📦 Dataset JSON chargé: ${data.length} segments`);
            } else {
              throw new Error(
                "Format JSON invalide. Attendu: {segments: [...]}",
              );
            }
          } else {
            // Format texte des résultats WER existants
            const lines = text.split("\n");

            for (const line of lines) {
              if (line.includes("WER:")) {
                // Parser le format: "phrase0001.wav - WER: 5.23% (Reference: ... Hypothesis: ...)"
                const match = line.match(/(\w+\.wav)\s+-\s+WER:\s+([\d.]+)%/);
                if (match) {
                  const [, segment, wer] = match;
                  const werValue = parseFloat(wer);

                  // Extraire les longueurs si disponibles
                  const refMatch = line.match(/Reference:\s*"([^"]+)"/);
                  const hypMatch = line.match(/Hypothesis:\s*"([^"]+)"/);

                  data.push({
                    segment: segment.replace(".wav", ""),
                    wer: werValue,
                    reference_text: refMatch ? refMatch[1] : "",
                    hypothesis_text: hypMatch ? hypMatch[1] : "",
                    reference_length: refMatch
                      ? refMatch[1].split(" ").length
                      : 0,
                    hypothesis_length: hypMatch
                      ? hypMatch[1].split(" ").length
                      : 0,
                    category:
                      werValue < 5
                        ? "excellent"
                        : werValue < 10
                          ? "good"
                          : werValue < 20
                            ? "fair"
                            : "poor",
                    status: "tested",
                  });
                }
              }
            }
          }

          // Sauvegarder dans le localStorage
          await this.saveWERData(data);

          resolve(data);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error("Erreur de lecture du fichier"));
      reader.readAsText(file);
    });
  },

  /**
   * Parse un fichier ZIP contenant les fichiers audio et texte
   * @param {File} zipFile - Fichier ZIP
   * @returns {Promise<Array>} Données avec audio en base64
   */
  async parseZipFile(zipFile) {
    try {
      const zip = await JSZip.loadAsync(zipFile);
      const segments = [];
      const audioFiles = {};
      const textFiles = {};

      // Extraire tous les fichiers
      for (const [filename, file] of Object.entries(zip.files)) {
        if (file.dir) continue;

        const basename = filename.split("/").pop();
        const name = basename.replace(/\.(wav|txt)$/, "");

        if (basename.endsWith(".wav")) {
          const arrayBuffer = await file.async("arraybuffer");
          const base64 = btoa(
            new Uint8Array(arrayBuffer).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              "",
            ),
          );
          audioFiles[name] = {
            filename: basename,
            base64,
            size: arrayBuffer.byteLength,
          };
        } else if (basename.endsWith(".txt")) {
          const text = await file.async("text");
          textFiles[name] = text.trim();
        }
      }

      // Créer les segments en appariant audio et texte
      for (const [name, audio] of Object.entries(audioFiles)) {
        if (textFiles[name]) {
          segments.push({
            segment: name,
            audio_file: audio.filename,
            audio_base64: audio.base64,
            audio_size: audio.size,
            reference_text: textFiles[name],
            reference_length: textFiles[name].split(" ").length,
            hypothesis_length: 0,
            wer: 0,
            category: "pending",
            status: "not_tested",
          });
        }
      }

      console.log(`📦 ZIP chargé: ${segments.length} segments trouvés`);

      // Ne pas sauvegarder les audio_base64 dans localStorage (trop volumineux)
      // Les segments avec audio_base64 restent en mémoire dans le composant
      console.log(
        "⚠️ Audio en mémoire uniquement (non sauvegardé dans localStorage)",
      );

      return segments;
    } catch (error) {
      console.error("Erreur lors de l'extraction du ZIP:", error);
      throw new Error("Impossible d'extraire le fichier ZIP");
    }
  },
};

export default werService;
